import type {
  ColumnDef as TanstackColumnDef,
  RowData,
  HeaderContext
} from '@tanstack/vue-table'
import { ref, type VNode, computed } from 'vue'
import { valueUpdater } from '../../lib/utils'
import type { ValidatedCustomStatusOptions } from '../../lib/tailwind-validation'
import DataTableCellPopover from './cells/DataTableCellPopover.vue'
import DataTableCellPopoverActions from './cells/DataTableCellPopoverActions.vue'
import DataTableCellPopoverContent from './cells/DataTableCellPopoverContent.vue'
import DataTableCellPopoverContentHeader from './cells/DataTableCellPopoverContentHeader.vue'
import DataTableCellPopoverRoot from './cells/DataTableCellPopoverRoot.vue'
import DataTableCellPopoverTrigger from './cells/DataTableCellPopoverTrigger.vue'
import DataTable, { type DataTableProps } from './DataTable.vue'
import DataTableActions, {
  type DataTableActionsProps
} from './DataTableActions.vue'
import DataTableColumnHeader from './DataTableColumnHeader.vue'
import DataTablePagination from './DataTablePagination.vue'
import DataTableViewOptions from './DataTableViewOptions.vue'

export * from './DataTable.vue'
export * from './DataTableActions.vue'
export * from './DataTableColumnHeader.vue'
export * from './DataTablePagination.vue'
export * from './DataTableViewOptions.vue'
export * from './cells/DataTableCellPopoverRoot.vue'
export * from './cells/DataTableCellPopoverContentHeader.vue'
export * from './cells/DataTableCellPopoverActions.vue'
export * from './cells/DataTableCellPopoverContent.vue'
export * from './cells/DataTableCellPopoverTrigger.vue'
export * from './cells/DataTableCellPopover.vue'
export {
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
  DataTableActions,
  DataTableCellPopoverRoot,
  DataTableCellPopoverContentHeader,
  DataTableCellPopoverActions,
  DataTableCellPopoverContent,
  DataTableCellPopoverTrigger,
  DataTableCellPopover
}

export type {
  Table,
  Column,
  SortingState,
  ColumnFiltersState,
  PaginationState,
  VisibilityState,
  ExpandedState,
  RowData
} from '@tanstack/vue-table'
export {
  FlexRender,
  getCoreRowModel,
  getSortedRowModel,
  useVueTable,
  getPaginationRowModel,
  getFilteredRowModel,
  getExpandedRowModel
} from '@tanstack/vue-table'

export type RowDef<TData extends RowData> = TData & {
  status?: 'loading' | 'new' | 'inactive'
}

export function isRowDef<TData>(row: unknown): row is RowDef<TData> {
  const rowDef = row as unknown as RowDef<TData>
  if (
    rowDef.status === 'loading' ||
    rowDef.status === 'new' ||
    rowDef.status === 'inactive'
  ) {
    return true
  }
  return false
}

export type GatColumnDef<
  TData extends RowData,
  TValue = unknown,
  ColId extends string = string
> = TanstackColumnDef<TData, TValue> & {
  /** Render the header of the column. */
  header?: (header: HeaderContext<TData, TValue>) => VNode
  /** The title of the column. */
  title?: string
}

export function isGatColumnDef<TData>(
  col: unknown
): col is GatColumnDef<TData> {
  return typeof (col as unknown as GatColumnDef<TData>).title !== 'undefined'
}

export function isDataTableActionsProps<TData>(
  props: DataTableActionsProps<TData> | VNode
): props is DataTableActionsProps<TData> {
  return typeof props === 'object' && props !== null && 'items' in props
}

export const defaultColumnSizing = {
  size: 150,
  minSize: 120,
  maxSize: Number.MAX_SAFE_INTEGER
}

export type CustomStatusOptions = ValidatedCustomStatusOptions

export type StatusColumnDef<TData extends RowData> = {
  /**
   * Strategy for determining if a row is loading.
   * - Function: Custom logic based on row data
   * - Array of IDs: Row IDs that are currently loading
   * - Key: Property name that contains loading state
   *
   * @example
   * ```typescript
   * // Function approach
   * isLoading: (row) => row.isProcessing
   *
   * // Array of IDs approach
   * isLoading: ['row-1', 'row-2', 'row-3']
   *
   * // Property key approach
   * isLoading: 'isLoading'
   * ```
   */
  isLoading?: string[]

  /**
   * Strategy for determining if a row has been updated.
   * - Function: Custom logic based on row data
   * - Array of IDs: Row IDs that have been updated
   * - Key: Property name that contains updated state
   *
   * @example
   * ```typescript
   * // Function approach
   * isUpdated: (row) => row.updatedAt > Date.now() - 60000
   *
   * // Array of IDs approach (recommended for dynamic updates)
   * isUpdated: ['company-123', 'company-456']
   *
   * // Property key approach
   * isUpdated: 'isUpdated'
   * ```
   */
  isUpdated?: string[]

  customStatus?: (row: TData) => CustomStatusOptions | undefined
}

export function getIsInactive<TData extends RowData>(
  row: TData,
  isRowInactive: DataTableProps<TData, unknown>['isRowInactive']
) {
  if (typeof isRowInactive === 'function' && isRowInactive(row)) {
    return true
  }
  if (
    typeof isRowInactive === 'string' &&
    typeof row[isRowInactive as keyof TData] === 'boolean' &&
    row[isRowInactive as keyof TData]
  ) {
    return true
  }
  return false
}

export function getIsUpdated<TData extends RowData>(
  row: TData,
  updatedRowIds: StatusColumnDef<TData>['isUpdated'],
  isRowUpdated: DataTableProps<TData, unknown>['isRowUpdated'],
  rowId?: string | number
) {
  if (typeof isRowUpdated === 'function' && isRowUpdated(row)) {
    return true
  }
  if (
    typeof isRowUpdated === 'string' &&
    typeof row[isRowUpdated as keyof TData] === 'boolean' &&
    row[isRowUpdated as keyof TData]
  ) {
    // Is a boolean and true.
    return true
  }
  if (
    Array.isArray(updatedRowIds) &&
    rowId !== undefined &&
    updatedRowIds.includes(String(rowId))
  ) {
    return true
  }

  return false
}

export function getIsLoading<TData extends RowData>(
  row: TData,
  loadingRowIds: StatusColumnDef<TData>['isLoading'],
  isRowLoading: DataTableProps<TData, unknown>['isRowLoading'],
  rowId?: string | number
) {
  if (typeof isRowLoading === 'function' && isRowLoading(row)) {
    return true
  }
  if (
    typeof isRowLoading === 'string' &&
    typeof row[isRowLoading as keyof TData] === 'boolean' &&
    row[isRowLoading as keyof TData]
  ) {
    return true
  }
  if (
    Array.isArray(loadingRowIds) &&
    rowId !== undefined &&
    loadingRowIds.includes(String(rowId))
  ) {
    return true
  }
  return false
}

/**
 * Composable for managing DataTable row statuses with v-model support
 * Provides a reactive interface for tracking updated, loading, and inactive rows
 */
export function useDataTableRowStatus<TData extends RowData>(options?: {
  isRowInactive?: DataTableProps<TData, unknown>['isRowInactive']
  customStatus?: StatusColumnDef<TData>['customStatus']
}) {
  const isUpdated = ref<string[]>([])
  const isLoading = ref<string[]>([])

  // Helper functions for managing statuses
  const markAsUpdated = (rowId: string) => {
    console.log('markAsUpdated', rowId)
    if (!isUpdated.value.includes(rowId)) {
      isUpdated.value.push(rowId)
    }
  }

  const clearUpdatedStatus = (rowId: string) => {
    isUpdated.value = isUpdated.value.filter((id) => id !== rowId)
  }

  const markAsLoading = (rowId: string) => {
    if (!isLoading.value.includes(rowId)) {
      isLoading.value.push(rowId)
    }
    return {
      clearLoadingStatus: () => {
        clearLoadingStatus(rowId)
      }
    }
  }

  const clearLoadingStatus = (rowId: string) => {
    isLoading.value = isLoading.value.filter((id) => id !== rowId)
  }

  const clearAllStatuses = () => {
    isUpdated.value = []
    isLoading.value = []
  }

  /**
   * Generic function to wrap optimistic updates and loading states
   * @param id - The ID of the row
   * @param fn - The operation to perform (function that takes the same parameters as the original)
   * @param originalData - The original data
   * @param dataSetter - The function to set the data
   * @param options - The options
   * @param options.markAsLoading - Whether to mark the row as loading
   * @default true
   * @returns Object with onSuccess and onError callbacks
   */
  function tryCommandOperation(
    id: string,
    fn: Function,
    originalData: TData[],
    dataSetter: (data: TData[]) => void,
    dataUpdater?: (data: TData) => void,
    options?: {
      markAsLoading?: boolean
    }
  ) {
    // Store the current state to revert on error
    const currentData = [...originalData]

    // Perform the optimistic update
    fn()
    if (options?.markAsLoading ?? true) {
      markAsLoading(id)
    }

    return {
      /**
       * Callback for successful operation
       * @param updatedValue - The updated value
       * @param options.markAsUpdated - Whether to mark the row as updated
       * @default true
       */
      onSuccess: (
        updatedValue?: TData,
        options?: { markAsUpdated?: boolean }
      ) => {
        clearLoadingStatus(id)
        if (options?.markAsUpdated ?? true) {
          markAsUpdated(id)
        }
        if (dataUpdater && updatedValue) {
          dataUpdater(updatedValue)
        }
      },
      onError: (errorMessage?: string) => {
        clearLoadingStatus(id)
        // Revert the optimistic update
        if (currentData) {
          dataSetter(currentData)
        }
        throw errorMessage
      }
    }
  }

  return {
    // Individual reactive arrays (for backward compatibility)
    isUpdated,
    isLoading,

    // Helper functions
    markAsUpdated,
    clearUpdatedStatus,
    markAsLoading,
    clearLoadingStatus,
    clearAllStatuses,

    // Legacy support
    isRowInactive: options?.isRowInactive,

    tryCommandOperation
  }
}

export { valueUpdater }
